import { SettingsService } from '@/services/settings-service'
import { backendSettingsService } from '@/services/backend-settings-service'
import { logError, logInfo } from '@/lib/logger'
import type { SettingsState } from '@/types/settings'

export interface DesktopAppPosition {
  row: number
  col: number
}

/**
 * Desktop Apps Service
 * Verwaltet welche Apps auf dem Desktop installiert sind
 * Nutzt User Settings (desktopInstalledApps und desktopIconPositions)
 */
export const desktopAppsService = {
  /**
   * Holt alle installierten Desktop-Apps für den aktuellen User.
   * Wirft, wenn die Settings nicht geladen werden konnten.
   */
  async getInstalledApps(): Promise<string[]> {
    // Kein Fallback: eine leere Liste hieße „nichts installiert" (#908).
    const settings = await SettingsService.loadUserSettings()
    return settings.desktopInstalledApps || []
  },

  /**
   * Installiert eine App auf dem Desktop
   */
  async installApp(appId: string, position?: DesktopAppPosition): Promise<void> {
    try {
      // Direkt gegen das Backend laden: schlägt der Load fehl, bricht die
      // Installation ab, statt mit Default-Settings fremde Keys zu zerstören
      const settings = await backendSettingsService.loadSettings()
      const installedApps = settings?.desktopInstalledApps || []

      // Füge App hinzu, falls noch nicht installiert
      if (!installedApps.includes(appId)) {
        const update: Partial<SettingsState> = {
          desktopInstalledApps: [...installedApps, appId],
        }

        // Wenn Position angegeben, speichere auch die Position
        if (position) {
          update.desktopIconPositions = {
            ...(settings?.desktopIconPositions || {}),
            [appId]: position,
          }
        }

        // PATCH statt Full-Save: nur die Desktop-Keys werden ersetzt
        const success = await backendSettingsService.patchSettings(update)
        if (!success) {
          throw new Error(`App ${appId} konnte nicht gespeichert werden`)
        }

        // Event auslösen für UI-Update
        window.dispatchEvent(new CustomEvent('settings-updated'))

        logInfo(`App ${appId} auf Desktop installiert`)
      }
    } catch (error) {
      logError(error as Error, { context: 'desktopAppsService.installApp', appId })
      throw error
    }
  },

  /**
   * Deinstalliert eine App vom Desktop
   */
  async uninstallApp(appId: string): Promise<void> {
    try {
      const settings = await backendSettingsService.loadSettings()
      const installedApps = settings?.desktopInstalledApps || []

      // Entferne App samt gespeicherter Position — die Deinstallation ist
      // der einzige Ort, an dem Positionen explizit entfernt werden
      const positions = { ...(settings?.desktopIconPositions || {}) }
      delete positions[appId]

      const success = await backendSettingsService.patchSettings({
        desktopInstalledApps: installedApps.filter(id => id !== appId),
        desktopIconPositions: positions,
      })
      if (!success) {
        throw new Error(`App ${appId} konnte nicht entfernt werden`)
      }

      // Event auslösen für UI-Update
      window.dispatchEvent(new CustomEvent('settings-updated'))

      logInfo(`App ${appId} vom Desktop deinstalliert`)
    } catch (error) {
      logError(error as Error, { context: 'desktopAppsService.uninstallApp', appId })
      throw error
    }
  },

  /**
   * Aktualisiert die Position einer Desktop-App
   * Nutzt das bestehende desktopIconPositions System
   */
  async updateAppPosition(
    appId: string,
    position: DesktopAppPosition
  ): Promise<void> {
    try {
      const settings = await backendSettingsService.loadSettings()
      const positions = {
        ...(settings?.desktopIconPositions || {}),
        [appId]: position,
      }

      const success = await backendSettingsService.patchSettings({
        desktopIconPositions: positions,
      })
      if (!success) {
        throw new Error(`Position für App ${appId} konnte nicht gespeichert werden`)
      }

      logInfo(`Position für App ${appId} aktualisiert`)
    } catch (error) {
      logError(error as Error, { context: 'desktopAppsService.updateAppPosition', appId })
      throw error
    }
  },

  /**
   * Prüft ob eine App auf dem Desktop installiert ist
   */
  async isAppInstalled(appId: string): Promise<boolean> {
    try {
      const apps = await this.getInstalledApps()
      return apps.includes(appId)
    } catch (error) {
      logError(error as Error, { context: 'desktopAppsService.isAppInstalled', appId })
      return false
    }
  },

  /**
   * Holt die Position einer Desktop-App
   */
  async getAppPosition(appId: string): Promise<DesktopAppPosition | null> {
    try {
      const settings = await SettingsService.loadUserSettings()
      const positions = settings.desktopIconPositions || {}
      return positions[appId] || null
    } catch (error) {
      logError(error as Error, { context: 'desktopAppsService.getAppPosition', appId })
      return null
    }
  },
}
